import React from "react";
import { push } from "connected-react-router";
import type { Props, State } from "./types";
import { connect } from "react-redux";
import { delayedDispatch, setBreadcrumb, setLoader, setTitle, updateCrumb } from "store/actions";
import { Helmet } from "react-helmet";
import { ContentWrapper, File as FileComponent } from "components";
import { Tabs, notification, Tooltip, Button } from "antd";
import { FileAddOutlined, FileOutlined, FileUnknownOutlined } from "@ant-design/icons";
import { API, Endpoints } from "utils/api";
import Strings from "utils/strings";
import "./styles.scss";

class HelpFiles extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);

		this.state = {
			files: [],
			selectedTab: "product"
		};
	}

	componentDidMount(): void {
		const { dispatch } = this.props;

		dispatch(setTitle(""));

		this.breadcrumb();
		this.getData();
	}

	componentDidUpdate(): void {
		const { dispatch } = this.props;
		dispatch(updateCrumb());
	}

	async getData() {
		const { dispatch } = this.props;

		dispatch(setLoader(true));

		try {
			const response = await API.get({
				url: Endpoints.uriFiles()
			});

			if (response.ok) {
				const { files = [] } = response.data.results || {};
				this.setState({ files });
			} else {
				notification.error({
					message: Strings.settings.files,
					description: response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err: unknown) {
			notification.error({
				message: Strings.serverErrors.title,
				description: err as string,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	async deleteFile(id: string) {
		const { dispatch } = this.props;

		dispatch(setLoader(true));

		try {
			const response = await API.delete({
				url: Endpoints.uriFiles(id)
			});

			if (response.ok) {
				await this.getData();

				notification.success({
					message: Strings.settings.files,
					description: response?.data?.message || Strings.settings.fileDeleted,
					placement: "bottomRight",
					duration: 5
				});
			} else {
				notification.error({
					message: Strings.settings.files,
					description: response?.data?.message || Strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err: unknown) {
			notification.error({
				message: Strings.serverErrors.title,
				description: err as string,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	breadcrumb() {
		delayedDispatch(
			setBreadcrumb(() => {
				return {
					locations: [
						{
							text: Strings.sidebar.settings,
							route: "/settings",
							icon: "preferences"
						},
						{
							text: Strings.settings.files,
							icon: <FileUnknownOutlined translate={null} />,
							iconType: "antd"
						}
					]
				};
			})
		);
	}

	getString(topic: string) {
		switch (topic) {
			case "product":
				return Strings.settings.topicProduct;
			case "business":
				return Strings.settings.topicBusiness;
			case "panflet":
				return Strings.settings.panflet;
			case "productFile":
				return Strings.settings.productFile;
			case "other":
				return Strings.settings.other;
			default:
				return topic;
		}
	}

	renderGallery() {
		const { files: apiFiles = [], selectedTab } = this.state;
		const { dispatch } = this.props;

		console.log('selectedTab', selectedTab);

		const filesArr = apiFiles.find((arr) => arr?._id === selectedTab);
		const files = filesArr?.files || [];
		const disableAdd = (selectedTab === "panflet" || selectedTab === "productFile") && files.length === 1;

		const languages = ["pt", "en", "fr"];

		return (
			<>
				<ContentWrapper>
					<Tabs
						defaultActiveKey="1"
						activeKey={selectedTab}
						onChange={(key: string) => this.setState({ selectedTab: key, })}
						tabBarExtraContent={
							<Tooltip title={disableAdd ? Strings.settings.onlyOneFileAllowed : ""}>
								<span>
									<button
										className="TabHeaderButton"
										disabled={disableAdd}
										onClick={() => dispatch(push("/settings/files/new", { topic: selectedTab }))}
									>
										<FileAddOutlined translate={null} />
										<p>{Strings.settings.addFile}</p>
									</button>
								</span>
							</Tooltip>
						}
					>
						<Tabs.TabPane tab={Strings.settings.topicProduct} key="product" />
						<Tabs.TabPane tab={Strings.settings.topicBusiness} key="business" />
						<Tabs.TabPane tab={Strings.settings.panflet} key="panflet" />
						<Tabs.TabPane tab={Strings.settings.productFile} key="productFile" />
						<Tabs.TabPane tab={Strings.settings.other} key="other" />
					</Tabs>
					{files.length === 0 ? (
						<div className="FileLevelHeader">
							<h3>{Strings.settings.noFiles}</h3>
						</div>
					) : (
						<div className="FileGrid">
							{files.map((file) => (
								<FileComponent key={file._id} onDelete={() => this.deleteFile(file._id!)} {...file} />
							))}
						</div>
					)}
				</ContentWrapper>
				{(selectedTab === "panflet" || selectedTab === "productFile") && (
					<ContentWrapper>
						<div>
							<div>
								<h3>{selectedTab === "panflet" ? Strings.settings.panflet : Strings.settings.productFile}</h3>
								{languages.map((lang) => (
									<div key={lang} style={{ display: "flex", flexDirection: "row", gap: 8 }}>
										<h4>{lang.toUpperCase()}:</h4>
										<a
											href={`https://essenciadavida.pt/${
												selectedTab === "panflet" ? "CatalogoDigital" : "FichasDeProduto"
											}/${lang}`}
										>
											essenciadavida.pt/{selectedTab === "panflet" ? "CatalogoDigital" : "FichasDeProduto"}/{lang}
										</a>
										<Button
											icon={<FileOutlined translate={null} />}
											size="small"
											type="primary"
											onClick={() => {
												navigator.clipboard.writeText(
													`https://essenciadavida.pt/${
														selectedTab === "panflet" ? "CatalogoDigital" : "FichasDeProduto"
													}/${lang}`
												);
												notification.success({
													message: Strings.settings.files,
													description: Strings.settings.linkCopied || "Link copied!",
													placement: "bottomRight",
													duration: 2
												});
											}}
										>
											{Strings.settings.copyLink || "Copy link"}
										</Button>
									</div>
								))}
							</div>
						</div>
					</ContentWrapper>
				)}
			</>
		);
	}

	render() {
		return (
			<React.Fragment>
				<Helmet>
					<title>{Strings.settings.files}</title>
					<meta name="description" content="Set the level of partner in order for your partners to get files" />
				</Helmet>
				{this.renderGallery()}
			</React.Fragment>
		);
	}
}

const mapStateToProps = (state: { language: string }) => ({
	language: state.language
});

export default connect(mapStateToProps)(HelpFiles);
